// src/components/admin/AdminNotes.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaSave, FaTrash, FaPlusSquare, FaSpinner } from 'react-icons/fa'; // Icons

const API_BASE = process.env.REACT_APP_API_URL;

const AdminNotes = () => {
  const [notes, setNotes] = useState([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setMessage('');
    console.log('AdminNotes: fetchNotes initiated.'); // DEBUG LOG
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('AdminNotes: No token found. User might not be logged in or token expired.'); // DEBUG LOG
        setMessage('Authentication error: Not logged in or token expired. Please log in as admin.');
        setLoading(false);
        return;
      }
      console.log('AdminNotes: Token found, proceeding with API call.'); // DEBUG LOG
      const headers = { Authorization: `Bearer ${token}` };

      // Attempting to fetch notes
      const res = await axios.get(`${API_BASE}/api/admin-notes`, {
        headers,
        withCredentials: true,
      });

      console.log('AdminNotes: API call successful. Response data:', res.data); // DEBUG LOG
      setNotes(res.data);
      // REMOVED: setMessage('Notes loaded successfully.'); // Removed this line as requested
    } catch (err) {
      console.error('AdminNotes: Error during API call:', err.response?.data || err.message, err.response?.status); // DEBUG LOG
      let errorMessage = '❌ Failed to load notes. Please check your backend connection.';
      if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = '❌ Failed to load notes. Access denied: Admins only. Please ensure you are logged in as an administrator.';
      } else if (err.response?.status === 404) {
        errorMessage = '❌ Failed to load notes. API route not found. Check server.js for /api/admin-notes.';
      } else if (err.response?.data?.message) {
        errorMessage = `❌ ${err.response.data.message}`;
      }
      setMessage(errorMessage);
      setNotes([]); // Ensure notes are cleared on error
    } finally {
      console.log('AdminNotes: fetchNotes completed. Setting loading to false.'); // DEBUG LOG
      setLoading(false);
    }
  }, []); // API_BASE removed from dependencies (as it's a static constant)

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSaveNote = async (e) => {
    e.preventDefault();
    setMessage('');
    console.log('AdminNotes: Attempting to save note.');

    if (!newNoteContent.trim()) {
      setMessage('Please type a note message before saving.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Authentication error: Please log in to save notes.');
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API_BASE}/api/admin-notes`, { title: newNoteTitle, content: newNoteContent }, {
        headers,
        withCredentials: true,
      });
      setMessage('✅ Note saved successfully!');
      setNewNoteContent('');
      setNewNoteTitle('');
      fetchNotes(); // Refresh the list of notes
      console.log('AdminNotes: Save successful, response:', res.data);
    } catch (err) {
      console.error('AdminNotes: Error saving note:', err.response?.data || err.message, err.response?.status);
      let errorMessage = '❌ Failed to save note. Please try again.';
      if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = '❌ Failed to save note. Access denied: Admins only.';
      } else if (err.response?.status === 404) {
        errorMessage = '❌ Failed to save note. API route not found. Check server.js for /api/admin-notes.';
      } else if (err.response?.data?.message) {
        errorMessage = `❌ ${err.response.data.message}`;
      }
      setMessage(errorMessage);
    }
  };

  const handleDeleteNote = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this note?');
    if (confirmDelete) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setMessage('Authentication error: Please log in to delete notes.');
          return;
        }
        const headers = { Authorization: `Bearer ${token}` };
        await axios.delete(`${API_BASE}/api/admin-notes/${id}`, {
          headers,
          withCredentials: true,
        });
        setMessage('✅ Note deleted successfully!');
        fetchNotes(); // Refresh the list of notes
        console.log(`AdminNotes: Note with ID ${id} deleted.`);
      } catch (err) {
        console.error('AdminNotes: Error deleting note:', err.response?.data || err.message);
        let errorMessage = '❌ Failed to delete note. Please try again.';
        if (err.response?.status === 401 || err.response?.status === 403) {
          errorMessage = '❌ Failed to delete note. Access denied: Admins only.';
        } else if (err.response?.data?.message) {
          errorMessage = `❌ ${err.response.data.message}`;
        }
        setMessage(errorMessage);
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Admin Notes</h3>

      {message && (
        <div className={`p-3 mb-4 rounded-md ${message.startsWith('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Add New Note */}
      <form onSubmit={handleSaveNote} className="mb-6 border-b pb-6 border-gray-200">
        <h4 className="text-lg font-medium text-gray-700 mb-3 flex items-center"><FaPlusSquare className="mr-2" /> Add New Note</h4>
        <input
          type="text"
          placeholder="Note Title (Optional)"
          value={newNoteTitle}
          onChange={(e) => setNewNoteTitle(e.target.value)}
          className="w-full p-2 border rounded-md mb-2 focus:ring-2 focus:ring-cyan-500"
        />
        <textarea
          placeholder="Type your note here..."
          value={newNoteContent}
          onChange={(e) => setNewNoteContent(e.target.value)}
          rows="4"
          className="w-full p-2 border rounded-md focus:ring-2 focus:ring-cyan-500"
          required
        ></textarea>
        <button
          type="submit"
          className="mt-3 px-5 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700 transition"
        >
          <FaSave className="inline-block mr-2" /> Save Note
        </button>
      </form>

      {/* List Existing Notes */}
      <h4 className="text-lg font-medium text-gray-700 mb-3">Existing Notes</h4>
      {loading ? (
        <div className="flex justify-center items-center h-24">
          <FaSpinner className="animate-spin text-cyan-600 text-3xl" />
          <p className="ml-3 text-gray-600">Loading notes...</p>
        </div>
      ) : notes.length === 0 ? (
        <p className="text-gray-500">No notes found yet.</p>
      ) : (
        <ul className="space-y-4">
          {notes.map(note => (
            <li key={note._id} className="p-4 bg-gray-50 border border-gray-200 rounded-md shadow-sm">
              {note.title && <p className="font-semibold text-gray-800 text-lg mb-1">{note.title}</p>}
              <p className="text-gray-700 text-sm mb-2">{note.content}</p>
              <p className="text-xs text-gray-500 mb-2">Last Updated: {new Date(note.updatedAt).toLocaleString()}</p>
              <button
                onClick={() => handleDeleteNote(note._id)}
                className="px-3 py-1 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 transition"
              >
                <FaTrash className="inline-block mr-1" /> Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminNotes;